import React, { ReactElement, ReactNode } from 'react';
import { Spinner } from '../icons/icons';

type TVariant = 'solid' | 'clean';
type TColors = 'primary' | 'secondary';
type TSize = 'sm' | 'md';

export interface ButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'color' | 'variant' | 'isLoading' | 'prefix' | 'size'
  > {
  type?: 'button' | 'reset' | 'submit';
  color?: TColors;
  variant?: TVariant;
  isLoading?: boolean;
  prefix?: React.ReactNode;
  size?: TSize;
}

const Prefix: React.FC<{
  size?: TSize;
  children?: ReactNode;
}> = ({ size, children }): ReactElement => {
  const getMarginClass = () => {
    switch (size) {
      case 'sm':
        return 'mr-1';
      case 'md':
        return 'mr-2';
      default:
        return '';
    }
  };
  return <div className={`flex w-4 h-4 ${getMarginClass()}`}>{children}</div>;
};

const Button = ({
  variant = 'solid',
  color = 'secondary',
  size = 'md',
  className = '',
  children,
  isLoading,
  prefix,
  ...props
}: ButtonProps) => {
  const getSizeClasses: () => string = () => {
    if (size !== 'sm') return 'h-12 px-4 py-1 text-lg';
    return 'h-8 p-2 text-sm';
  };

  const getBordersAndColors = () => {
    if (color === 'primary') {
      return `text-primary  bg-transparent ${
        variant === 'solid'
          ? 'border-primary hover:bg-primary hover:text-light'
          : 'border-transparent'
      }`;
    }
    return ` bg-transparent ${
      variant === 'solid'
        ? 'border-light hover:bg-secondary hover:text-light'
        : 'border-transparent'
    }`;
  };

  return (
    <button
      className={`
      inline-flex
      items-center 
      justify-center
      m-0
      border 
      rounded-3xl
      text-center 
      whitespace-nowrap 
      overflow-hidden 
      cursor-pointer
      text-ellipsis
      ${getSizeClasses()}
      ${getBordersAndColors()}
      ${className}
      `}
      type="button"
      {...props}
    >
      {prefix && <Prefix size={size}>{prefix}</Prefix>}
      {isLoading ? (
        <>
          {children}
          <Spinner className="ml-2 w-4 h-4 text-inherit" />
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
